import { Grid, Skeleton } from '@mui/material';
import { GridProps } from '@mui/material';
import { FC, ReactNode, forwardRef } from 'react';
import { ReactSortable, SortableEvent } from 'react-sortablejs';
import { PictureItemType } from './PictureEditDrawer';
import { useApiMutation } from '../../../../../query/hooks';
import { useSnackbar } from '../../../../../store/snackbar';
import { useIntl } from 'react-intl';
import { graphql } from '../../../../../gql';
import { useSortableLocalUpdate } from '../../../../utils/useSortableLocalUpdate';
import { PictureItem } from './PictureItem';
import { DND_DELAY } from '../../DetailTable';

const updatePictureOrderMutation = graphql(`
	mutation picturesDrawer_updatePictureOrder($activityId: UUID!, $pictures: [ActivityUpdatePicturesEntityRelationInput!]) {
		updateActivity(by: { id: $activityId }, data: { pictures: $pictures }) {
			ok
			errorMessage
		}
	}
`);

type Props = Readonly<{
	activityId: string;
	pictures: PictureItemType[];
	uploadingFiles: ReactNode[];
	isContentLoading: boolean;
}>;

export const PictureEditDrawerGrid: FC<Props> = ({ activityId, pictures, uploadingFiles, isContentLoading }) => {
	const intl = useIntl();
	const { state: picturesState, setState: setPicturesState, optimisticReorder, updateFailed } = useSortableLocalUpdate(pictures);
	const { openSnackbar } = useSnackbar();

	const { mutate: updatePictureOrder } = useApiMutation(updatePictureOrderMutation, {
		onError: () => {
			openSnackbar({
				open: true,
				message: intl.formatMessage({ id: 'common.unexpected-error' }),
				variant: 'alert',
				alert: {
					color: 'error',
				},
				close: true,
			});
		},
	});
	const handleReorderPictures = (event: SortableEvent) => {
		const resultStateCopy = optimisticReorder(event);

		if (resultStateCopy?.length != null) {
			setPicturesState(resultStateCopy);
			updatePictureOrder(
				{
					pictures: resultStateCopy.map((w, index) => ({
						update: {
							by: {
								id: w.id,
							},
							data: {
								order: index,
							},
						},
					})),
					activityId,
				},
				{
					onSettled: (result) => {
						result?.updateActivity.errorMessage && console.error(result.updateActivity.errorMessage);
						updateFailed(result?.updateActivity.ok);
					},
				},
			);
		}
	};

	return isContentLoading ? (
		<MuiGridRef>
			<Skeleton animation={'wave'} variant={'rectangular'} width={'100%'} height={'100%'} />
			<Skeleton animation={'wave'} variant={'rectangular'} width={'100%'} height={'100%'} />
		</MuiGridRef>
	) : (
		<ReactSortable
			tag={MuiGridRef}
			delayOnTouchOnly
			delay={DND_DELAY}
			list={picturesState as PictureItemType[]}
			setList={() => {}}
			onEnd={(event) => handleReorderPictures(event)}
		>
			<>
				{picturesState.map((picture) => (
					<PictureItem key={picture.id} picture={picture} sortable />
				))}
				{uploadingFiles}
			</>
		</ReactSortable>
	);
};

const MuiGridRef = forwardRef<HTMLDivElement, GridProps>((props, ref) => (
	<Grid
		display={'grid'}
		container
		width={'100%'}
		gridTemplateColumns={'1fr 1fr'}
		gridAutoRows={{ xs: 125, sm: 185 }}
		gap={1.5}
		{...props}
		ref={ref}
	/>
));
